import React, { useEffect } from 'react';
import AnimatedSection from './AnimatedSection';
import { CheckIcon } from './Icons';

const benefits = [
  { text: 'Schnelle Buchung', icon: '⚡', desc: 'In unter 2 Minuten' },
  { text: 'Flexible Zeiten', icon: '📅', desc: 'Auch am Wochenende' },
  { text: 'Sofortige Bestätigung', icon: '✓', desc: 'Per E-Mail' }
];

const Booking = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://asset-tidycal.b-cdn.net/js/embed.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <section
      id="buchung"
      data-testid="booking-section"
      className="py-24 md:py-32 bg-brand-light relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-brand-gold/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 right-0 w-64 h-64 bg-brand-gold/5 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <AnimatedSection className="text-center mb-16">
          <span className="inline-flex items-center gap-2 font-work text-sm text-brand-gold uppercase tracking-widest mb-4">
            <span className="w-8 h-px bg-brand-gold" />
            Online Terminbuchung
            <span className="w-8 h-px bg-brand-gold" />
          </span>
          <h2 className="font-marcellus text-4xl md:text-5xl lg:text-6xl text-brand-dark mb-6">
            Termin <span className="gradient-text">vereinbaren</span>
          </h2>
          <p className="font-work text-brand-dark/60 max-w-2xl mx-auto text-lg">
            Wählen Sie Ihren Wunschtermin bequem online. Einfach, schnell und unkompliziert.
          </p>
          <p className="font-work text-brand-dark/60 max-w-2xl mx-auto text-lg mt-2">
            Die Behandlung findet bei Ihnen zu Hause statt.
          </p>
        </AnimatedSection>

        <div className="grid lg:grid-cols-3 gap-12">
          {/* Benefits sidebar */}
          <AnimatedSection direction="left" className="lg:col-span-1">
            <div className="bg-white rounded-3xl p-8 shadow-lg sticky top-32 overflow-hidden relative border border-brand-gold/10">
              {/* Decorative circles */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-brand-gold/5 rounded-full blur-2xl" />
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-brand-gold/5 rounded-full blur-2xl" />
              
              <h3 className="font-marcellus text-2xl mb-8 relative z-10 text-brand-dark">Vorteile</h3>
              
              <div className="space-y-6 relative z-10">
                {benefits.map((benefit, index) => (
                  <div
                    key={index}
                    data-testid={`booking-benefit-${index}`}
                    className="flex items-start gap-4 group"
                  >
                    <div className="w-12 h-12 bg-brand-gold/10 rounded-2xl flex items-center justify-center text-xl group-hover:bg-brand-gold group-hover:scale-110 transition-all duration-300">
                      {benefit.icon}
                    </div>
                    <div>
                      <span className="font-work font-medium text-brand-dark block">{benefit.text}</span>
                      <span className="font-work text-sm text-brand-dark/50">{benefit.desc}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Trust badge */}
              <div className="mt-8 pt-8 border-t border-brand-dark/10 relative z-10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-500/10 rounded-full flex items-center justify-center">
                    <CheckIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-work text-sm text-brand-dark">Sichere Buchung</p>
                    <p className="font-work text-xs text-brand-dark/50">SSL-verschlüsselt</p>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>

          {/* TidyCal Embed */}
          <AnimatedSection direction="right" delay={200} className="lg:col-span-2">
            <div className="bg-white rounded-3xl shadow-xl overflow-hidden min-h-[600px] relative">
              {/* Header bar */}
              <div className="bg-brand-light px-6 py-4 border-b border-brand-dark/5">
                <div className="flex items-center gap-3">
                  <div className="flex gap-1.5">
                    <span className="w-3 h-3 rounded-full bg-red-400" />
                    <span className="w-3 h-3 rounded-full bg-yellow-400" />
                    <span className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <span className="font-work text-sm text-brand-dark/50">Terminkalender</span>
                </div>
              </div>
              
              <div className="p-4">
                <div
                  className="tidycal-embed"
                  data-path="alexandersuslov/bodyharmonic"
                  data-testid="tidycal-embed"
                />
              </div>
            </div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  );
};

export default Booking;
